import React, { useState } from 'react';
import axios from 'axios';
import Botpoison from '@botpoison/browser';
import { GeoapifyGeocoderAutocomplete, GeoapifyContext } from '@geoapify/react-geocoder-autocomplete';
import '@geoapify/geocoder-autocomplete/styles/minimal.css';
import {
  CheckCircle,
  Star,
  Phone,
  Mail,
  MapPin,
  Clock,
  DollarSign,
  Shield,
  Users,
  ChevronDown,
  Instagram,
  Facebook,
  Linkedin,
  Building
} from 'lucide-react';

// Import local images
import asisImage from './assets/asis.png';
import closingImage from './assets/closing.jpg';

// Initialize Botpoison
const botpoison = new Botpoison({
  publicKey: "pk_b8a10263-d7d9-4631-ac3a-943f0fe306ac"
});

// Geoapify API configuration
const GEOAPIFY_API_KEY = 'df6d8ed1c51b415e9ab9052a499f52e4';

// Add custom styles for the Geoapify component
const geoapifyStyles = `
  .geocoder-container {
    width: 100%;
  }
  .geoapify-autocomplete-input {
    width: 100%;
    padding: 12px;
    border: 1px solid var(--color-gray-300);
    border-radius: var(--radius-m);
    font-size: 16px;
    height: 48px;
    background: var(--color-white);
    transition: all var(--transition-normal);
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }
  .geoapify-autocomplete-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(62,120,140,0.2);
  }
  .geoapify-autocomplete-items {
    background: white;
    border: 1px solid var(--color-gray-300);
    border-radius: var(--radius-m);
    box-shadow: var(--shadow-2);
    z-index: 1000;
    margin-top: 4px;
  }
  .geoapify-autocomplete-item {
    padding: 12px;
    cursor: pointer;
    transition: all var(--transition-fast);
  }
  .geoapify-autocomplete-item:hover {
    background: var(--color-gray-50);
  }
`;

interface HomePageProps {
  showScrollTop: boolean;
  scrollToTop: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ showScrollTop, scrollToTop }) => {
  const [homeAddress, setHomeAddress] = useState('');
  const [activeQuestion, setActiveQuestion] = useState<number | null>(null);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    property: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleHeroPlaceSelect = (place: any) => {
    console.log('Hero selected place:', place);
    if (place?.properties?.formatted) {
      setHomeAddress(place.properties.formatted);
      // Also update the contact form
      setContactForm(prev => ({
        ...prev,
        property: place.properties.formatted
      }));
    }
  };

  const handlePlaceSelect = (place: any) => {
    console.log('Selected place:', place);
    if (place?.properties?.formatted) {
      setContactForm(prev => ({
        ...prev,
        property: place.properties.formatted
      }));
    }
  };

  const handleSuggestionChange = (suggestions: any[]) => {
    console.log('New suggestions:', suggestions);
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Basic validation
      if (!contactForm.name.trim() || !contactForm.email.trim()) {
        setSubmitMessage('Please fill in all required fields.');
        setIsSubmitting(false);
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactForm.email)) {
        setSubmitMessage('Please enter a valid email address.');
        setIsSubmitting(false);
        return;
      }

      // Process Botpoison challenge
      const { solution } = await botpoison.challenge({});

      // Submit to Formspark
      await axios.post('https://submit-form.com/y1kH0fSVQ', {
        ...contactForm,
        form_type: 'Contact Form',
        _botpoison: solution,
        _email: {
          to: 'contact@giardifamilyhomes.com',
          subject: 'New Contact Form Submission from John and Sofia Site'
        }
      });

      setSubmitMessage('Thank you for your message! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', phone: '', property: '', message: '' });
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitMessage('Sorry, there was an error sending your message. Please call us directly at (650) 485-1233 or try again.');
    }

    setIsSubmitting(false);
  };

  const handleContactInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const faqData = [
    {
      question: "Will I be under any form of obligation with your free cash offer?",
      answer: "Absolutely not! Our cash offer comes with zero obligation. You can take your time to review it, and there's no pressure to accept. If it doesn't work for you, simply let us know."
    },
    {
      question: "Is it still possible to sell my house to you even if it's listed with a real estate agent?",
      answer: "Yes, but you'll need to work with your agent to cancel the listing first. Once that's handled, we can move forward with our direct purchase process."
    },
    {
      question: "Will you still buy my house even if I owe money on it?",
      answer: "Yes! We regularly purchase homes with existing mortgages. We'll work directly with your lender to handle the payoff as part of the closing process."
    },
    {
      question: "How long do I have to wait before I can sell my house?",
      answer: "You can sell immediately! We provide cash offers within 24 hours and can close in as little as 7 days, or on your preferred timeline."
    },
    {
      question: "How much will you pay for my house?",
      answer: "We provide fair, competitive cash offers based on current market conditions, your home's condition, and recent comparable sales in your area. Every offer is customized to your specific property."
    },
    {
      question: "Will there be any charges when I sell my house to you?",
      answer: "No hidden fees, commissions, or closing costs on your end. We handle all the paperwork and associated costs, so you keep more of your money."
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-background-image"></div>
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="text-hero hero-title">
                Northern California's<br />
                Most Trusted Home Buyer
              </h1>
              <p className="text-large mt-l hero-subtitle">
                Trusted. Competitive. Local.<br />
                As a family-owned business, we proudly serve our community with fast, fair, all-cash offers—delivered in 24 hours or less.
              </p>
              <div className="hero-search">
                <div className="hero-search-wrapper">
                  <div className="hero-search-container">
                    <style>{geoapifyStyles}</style>
                    <GeoapifyContext apiKey={GEOAPIFY_API_KEY}>
                      <GeoapifyGeocoderAutocomplete
                        placeSelect={handleHeroPlaceSelect}
                        placeholder="Enter your home address"
                        value={homeAddress}
                        debounceDelay={100}
                      />
                    </GeoapifyContext>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      const contactSection = document.getElementById('contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  >
                    GET YOUR OFFER
                  </button>
                </div>
              </div>
              <div className="hero-contact mt-xl">
                <a href="tel:+16504851233" className="btn btn-secondary btn-large">
                  <Phone size={20} />
                  Call or Text (650) 485-1233
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="trust-signals">
        <div className="container">
          <div className="trust-content">
            <p className="text-small text-gray text-center mb-xl">
              Trusted by hundreds of Bay Area homeowners since 2020
            </p>
            <div className="trust-stats">
              <div className="trust-stat">
                <div className="stat-number">200+</div>
                <div className="stat-label">Homes Purchased</div>
              </div>
              <div className="trust-stat">
                <div className="stat-number">24hr</div>
                <div className="stat-label">Average Response</div>
              </div>
              <div className="trust-stat">
                <div className="stat-number">98%</div>
                <div className="stat-label">Client Satisfaction</div>
              </div>
              <div className="trust-stat">
                <div className="stat-number">7 Days</div>
                <div className="stat-label">Average Closing</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="value-prop section">
        <div className="container text-center">
          <h2 className="text-section-title">
            Sell Fast. Get Paid in 7 Days.
          </h2>
          <p className="text-large mt-l mb-xxl">
            No Repairs, No Fees, No Hassles. Just Cash.
          </p>

          <div className="benefits-grid">
            <div className="benefit-item">
              <div className="benefit-image">
                <img
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                  alt="Cash and calculator"
                  className="benefit-img"
                />
              </div>
              <div className="benefit-content">
                <DollarSign size={48} className="benefit-icon" />
                <h3 className="text-subsection-title">No Fees or Commission</h3>
                <p className="text-body text-gray">
                  Keep more of your money. No realtor commissions, closing costs, or hidden fees.
                </p>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-image">
                <img
                  src={asisImage}
                  alt="House sold as-is"
                  className="benefit-img"
                />
              </div>
              <div className="benefit-content">
                <Shield size={48} className="benefit-icon" />
                <h3 className="text-subsection-title">No Repair Costs</h3>
                <p className="text-body text-gray">
                  We buy homes "as-is", saving you from costly renovations and inspections.
                </p>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-image">
                <img
                  src={closingImage}
                  alt="Flexible closing calendar"
                  className="benefit-img"
                />
              </div>
              <div className="benefit-content">
                <Clock size={48} className="benefit-icon" />
                <h3 className="text-subsection-title">Flexible Closing Date</h3>
                <p className="text-body text-gray">
                  Pick the Date That Works Best for You—We'll Handle the Rest
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="process section-gray">
        <div className="container">
          <h2 className="text-section-title text-center mb-xxl">
            How It Works - Simple 3-Step Process
          </h2>

          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3 className="text-subsection-title">Complete the Form</h3>
                <p className="text-body text-gray">
                  Fill out our simple form with your property details. It takes less than 2 minutes.
                </p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3 className="text-subsection-title">Get Your Cash Offer</h3>
                <p className="text-body text-gray">
                  Receive a fair, no-obligation cash offer within 24 hours. No pressure to accept.
                </p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3 className="text-subsection-title">Close & Get Paid</h3>
                <p className="text-body text-gray">
                  Choose your closing date and get your cash. It's that simple!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="testimonials section-gray">
        <div className="container">
          <h2 className="text-section-title text-center mb-xxl">
            What Our Clients Say
          </h2>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="stars mb-m">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="text-body">
                "We got offers from other companies that were devastatingly low—almost hopeless. Then we found John and Sophia at Giardi Family Homes. Not only did they help us avoid foreclosure, but they gave us a fair price and even made sure we walked away with a little extra. We truly didn't think that was possible until we reached out to them."
              </p>
              <div className="testimonial-author mt-l">
                <strong>Carlos M.</strong>
                <span className="text-gray"> - Oakland, CA</span>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="stars mb-m">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="text-body">
                "We were honestly shocked—in a good way. After getting low offers from others, John and Sophia came in with a fair price, no pressure, and total professionalism. Selling our home ended up being easier than we ever expected."
              </p>
              <div className="testimonial-author mt-l">
                <strong>Ravi D.</strong>
                <span className="text-gray"> - Orinda, CA</span>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="stars mb-m">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="text-body">
                "Selling our home to Giardi Family Homes was one of the best decisions we've made. From start to finish, the process was smooth, respectful, and professional. John and the team were incredibly attentive, honest, and easy to work with. It's rare to come across people in real estate—or any industry—who care this much. In fact, it's hard to think of a professional team I'd more highly recommend than Giardi Family Homes."
              </p>
              <div className="testimonial-author mt-l">
                <strong>David F.</strong>
                <span className="text-gray"> - San Carlos, CA</span>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="stars mb-m">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="text-body">
                "From the moment we spoke with John and Sophia, we felt at ease. They weren't pushy or salesy—instead, they genuinely listened to what we needed and worked with us every step of the way. Selling our home can be emotional, but they made it simple, fair, and stress-free. Their warmth, professionalism, and follow-through were unmatched. What stood out most was how much they truly cared—not just about buying the home, but about making sure it was a good experience for our family. We're so grateful we chose to work with Giardi Family Homes, and we wouldn't hesitate to recommend them to anyone looking for a smooth and respectful way to sell."
              </p>
              <div className="testimonial-author mt-l">
                <strong>Alexa D.</strong>
                <span className="text-gray"> - San Jose, CA</span>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="stars mb-m">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="text-body">
                "We got offers from other companies that were devastatingly low—almost hopeless. Then we found John and Sophia at Giardi Family Homes. Not only did they help us avoid foreclosure, but they gave us a fair price and even made sure we walked away with a little extra. We truly didn't think that was possible until we reached out to them."
              </p>
              <div className="testimonial-author mt-l">
                <strong>Meghan O.</strong>
                <span className="text-gray"> - San Rafael, CA</span>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="stars mb-m">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <p className="text-body">
                "If you're reading this, you're probably in a situation like we were. We owed money on our home and spoke with nearly a dozen home-buying companies—every single one offered so little, we were going to walk away with nothing. On top of that, we needed to be out in less than 30 days. Then Giardi Family Homes stepped in. John and Sophia not only treated us with respect, but they got us a fair deal and made sure we closed fast—with money still in our pocket. We're beyond grateful we found them."
              </p>
              <div className="testimonial-author mt-l">
                <strong>Patrick C.</strong>
                <span className="text-gray"> - San Mateo</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq section">
        <div className="container">
          <h2 className="text-section-title text-center mb-xxl">
            Frequently Asked Questions
          </h2>

          <div className="faq-list">
            {faqData.map((item, index) => (
              <div key={index} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => setActiveQuestion(activeQuestion === index ? null : index)}
                  aria-expanded={activeQuestion === index}
                >
                  <span>{item.question}</span>
                  <ChevronDown
                    size={20}
                    className={`faq-icon ${activeQuestion === index ? 'faq-icon-open' : ''}`}
                  />
                </button>
                <div className={`faq-answer ${activeQuestion === index ? 'faq-answer-open' : ''}`}>
                  <p className="text-body text-gray">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="offer" className="cta section-gray">
        <div className="container text-center">
          <h2 className="text-section-title">
            Ready to Unlock Your Cash Offer?
          </h2>
          <p className="text-large text-gray mt-l mb-xl">
            Join hundreds of satisfied homeowners who chose the fast, easy way to sell.
          </p>
          <div className="cta-buttons">
            <a href="#contact" className="btn btn-primary btn-large">
              Unlock Your Cash Offer
            </a>
            <a href="tel:+16504851233" className="btn btn-secondary btn-large">
              <Phone size={20} />
              Call Now
            </a>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="contact section">
        <div className="container">
          <h2 className="text-section-title text-center mb-xxl">Get In Touch</h2>

          <div className="contact-card">
            {/* Left Side - Contact Form */}
            <div className="contact-form-section">
              <h3 className="text-subsection-title mb-l">
                Unlock Your Cash Offer
              </h3>
              <p className="text-body text-gray mb-xl">
                Ready to sell your house fast? Fill out the form below for your free, no-obligation cash offer.
              </p>

              <form onSubmit={handleContactSubmit}>
                <div className="form-group">
                  <label htmlFor="contact-name" className="form-label">Name</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    className="form-input"
                    value={contactForm.name}
                    onChange={handleContactInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-email" className="form-label">Email</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    className="form-input"
                    value={contactForm.email}
                    onChange={handleContactInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-phone" className="form-label">Phone</label>
                  <input
                    type="tel"
                    id="contact-phone"
                    name="phone"
                    className="form-input"
                    value={contactForm.phone}
                    onChange={handleContactInputChange}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-property" className="form-label">Property Address</label>
                  <div style={{ position: 'relative' }}>
                    <style>{geoapifyStyles}</style>
                    <GeoapifyContext apiKey={GEOAPIFY_API_KEY}>
                      <GeoapifyGeocoderAutocomplete
                        placeSelect={handlePlaceSelect}
                        suggestionsChange={handleSuggestionChange}
                        placeholder="Enter your property address"
                        value={contactForm.property}
                        debounceDelay={100}
                      />
                    </GeoapifyContext>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="contact-message" className="form-label">Message (Optional)</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    className="form-input form-textarea"
                    value={contactForm.message}
                    onChange={handleContactInputChange}
                    placeholder="Tell us about your situation..."
                    disabled={isSubmitting}
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary btn-large" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner"></div>
                      Sending...
                    </>
                  ) : (
                    'Unlock My Cash Offer'
                  )}
                </button>
              </form>

              {submitMessage && (
                <p className={`text-small mt-m ${submitMessage.includes('error') ? 'text-red' : 'text-success'}`}>
                  {submitMessage}
                </p>
              )}
            </div>

            {/* Right Side - Map and Contact Info */}
            <div className="contact-info-section">
              {/* Map */}
              <div className="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3166.8567285716766!2d-122.31154168469072!3d37.54491707979943!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808f9f4b8a5c7b8d%3A0x1234567890abcdef!2s216%20S%20Idaho%20St%2C%20San%20Mateo%2C%20CA%2094401!5e0!3m2!1sen!2sus!4v1703123456789!5m2!1sen!2sus"
                  width="100%"
                  height="200"
                  style={{ border: 0, borderRadius: 'var(--radius-m)' }}
                  allowFullScreen={true}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                ></iframe>
              </div>

              {/* Contact Info */}
              <div className="contact-details">
                <div className="contact-methods">
                  <div className="contact-method">
                    <Phone size={20} className="contact-icon" />
                    <div>
                      <strong>Call or Text</strong>
                      <p>(650) 485-1233</p>
                    </div>
                  </div>

                  <div className="contact-method">
                    <Mail size={20} className="contact-icon" />
                    <div>
                      <strong>Email</strong>
                      <p>contact@giardifamilyhomes.com</p>
                    </div>
                  </div>

                  <div className="contact-method">
                    <MapPin size={20} className="contact-icon" />
                    <div>
                      <strong>Office</strong>
                      <p>216 S Idaho Street<br />San Mateo, CA 94401</p>
                    </div>
                  </div>
                </div>

                {/* <div className="social-links">
                  <h5>Follow Us</h5>
                  <div className="social-icons-compact">
                    <a href="https://www.instagram.com/johnschrupre/" target="_blank" rel="noopener noreferrer" className="social-icon-compact">
                      <Instagram size={20} />
                    </a>
                    <a href="https://www.facebook.com/john.schrup.1" target="_blank" rel="noopener noreferrer" className="social-icon-compact">
                      <Facebook size={20} />
                    </a>
                    <a href="https://www.linkedin.com/in/john-schrup-598b02153/" target="_blank" rel="noopener noreferrer" className="social-icon-compact">
                      <Linkedin size={20} />
                    </a>
                    <a href="https://www.zillow.com/profile/jcschrup" target="_blank" rel="noopener noreferrer" className="social-icon-compact">
                      <Building size={20} />
                    </a>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          className="scroll-to-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </>
  );
};

export default HomePage; 